/**
 * <AuditBackfillBlock> — Settings > Advanced action to backfill
 * created_by / updated_by / created_at / updated_at on legacy records.
 *
 * Safe to click multiple times: the endpoint only fills fields that are
 * missing/blank. All new writes are auto-stamped by the backend middleware.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { History, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";

export default function AuditBackfillBlock() {
  const { activeWorkspaceId } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/audit/backfill`);
      const total = (data.details || []).reduce(
        (sum, d) =>
          sum +
          (d.created_at_filled || 0) +
          (d.updated_at_filled || 0) +
          (d.created_by_filled || 0) +
          (d.updated_by_filled || 0),
        0,
      );
      setResult({ ...data, total });
      toast.success(
        total > 0
          ? `Stamped ${total.toLocaleString()} audit values across ${data.collections_touched} collections`
          : "All records already have audit info",
      );
    } catch (e) {
      toast.error(apiErrorMessage(e, "Backfill failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border p-6 space-y-4" data-testid="settings-audit-backfill">
      <div className="flex items-center gap-2">
        <History className="w-4 h-4 text-muted-foreground" />
        <div className="label-eyebrow">Audit & Governance</div>
      </div>
      <p className="text-sm text-muted-foreground">
        All new records automatically capture <span className="font-medium">Created by</span> /
        {" "}<span className="font-medium">Updated by</span> along with timestamps. Use this
        one-time action to backfill sensible defaults on legacy records that were created before
        the audit system was rolled out.
      </p>
      <Button
        onClick={run}
        variant="outline"
        className="rounded-none"
        disabled={busy}
        data-testid="settings-audit-backfill-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <History className="w-4 h-4 mr-2" />}
        {busy ? "Backfilling…" : "Backfill audit fields"}
      </Button>
      {result && (
        <div className="border border-border/70 p-4 bg-secondary/40 text-xs space-y-2" data-testid="settings-audit-backfill-summary">
          <div className="font-medium">
            {result.total > 0
              ? `Stamped ${result.total.toLocaleString()} audit values across ${result.collections_touched} collections.`
              : "Nothing to backfill — everything already has audit info."}
          </div>
          {result.total > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 max-h-40 overflow-auto">
              {(result.details || []).slice(0, 40).map((d) => (
                <div key={d.collection} className="flex justify-between border-b border-border/40 py-1">
                  <span className="font-mono text-[11px] opacity-80">{d.collection}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {(d.created_at_filled || 0) + (d.updated_at_filled || 0) + (d.created_by_filled || 0) + (d.updated_by_filled || 0)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
